import { useCallback, useState } from 'react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { FileDownIcon } from 'lucide-react';
import { downloadPersonaPdf, type Persona } from '@/entities/persona';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { useTranslation } from 'react-i18next';
import { track } from '@/shared/analytics';

interface PersonaExportButtonProps {
  persona: Persona | null;
  workspaceId: string | null | undefined;
}

/**
 * "Download PDF" affordance in the audience-detail header. Fetches the
 * server-rendered PDF (headless print page → `GET /personas/:id/pdf`) and saves
 * it.
 *
 * Renders nothing without a persona and a workspace to export against, so the
 * header can hand it a slot unconditionally rather than every call site
 * restating the same conditions.
 */
export const PersonaExportButton = ({ persona, workspaceId }: PersonaExportButtonProps) => {
  const { t } = useTranslation('audiences');

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (isDownloading || !persona || !workspaceId) return;
    setIsDownloading(true);
    const notifId = notificationService.showInfo(
      t('audienceDetail.personaExportButton.preparingYourDownloadThis'),
      { persist: true },
    );
    try {
      const blob = await downloadPersonaPdf(persona.id, { workspaceId });
      // eslint-disable-next-line i18next/no-literal-string -- download filename slug, sanitized for the file system
      const fileName = `${sanitizeDownloadFileName(persona.segmentName ?? '', 'persona')}.pdf`;
      await downloadFile({ blob, mimeType: 'application/pdf', fileName });
      track('document_downloaded', {
        document_type: 'persona',
        format: 'pdf',
        downloader: 'loc',
      });
    } catch (err) {
      logger.error('Failed to download persona PDF', err);
      notificationService.showError(
        t('audienceDetail.personaExportButton.failedToDownloadPersona'),
      );
    } finally {
      notificationService.close(notifId);
      setIsDownloading(false);
    }
  }, [isDownloading, persona, workspaceId, t]);

  if (!persona || !workspaceId) return null;

  return (
    <Button
      variant="outline"
      onClick={() => void handleDownload()}
      disabled={isDownloading}
      // The visible word says which direction; the accessible name also says
      // what comes down and in what format, which the header has no room for.
      aria-label={t('audienceDetail.personaExportButton.downloadPersonaAsPdf')}
    >
      {isDownloading ? <Spinner size="sm" /> : <FileDownIcon className="size-4" />}
      {t('audienceDetail.personaExportButton.download')}
    </Button>
  );
};
